import React, { useState, useEffect } from "react";
import { api } from "../api";
import Icon from "./Icon";
import { StatusBadge, PriorityBadge } from "./Badge";
import Skeleton from "./Skeleton";
import { EmptyState } from "./State";
import Modal from "./Modal";
import { FormattedDescription } from "./ticket/FormattedDescription";
import { RCAPanel } from "./ticket/RCAPanel";
import { IncidentsPanel } from "./ticket/IncidentsPanel";
import { CommentsPanel } from "./ticket/CommentsPanel";

export { FormattedDescription, RCAPanel, IncidentsPanel, CommentsPanel };

const STATUS_TRANSITIONS = {
  open: ["in_progress"],
  in_progress: ["resolved", "open"],
  resolved: ["closed", "in_progress"],
  closed: [],
  deleted: [],
};

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  closed: "Closed",
  deleted: "Deleted",
};

export default function TicketDetailModal({ ticket, onClose, onUpdated }) {
  const [currentTicket, setCurrentTicket] = useState(ticket);
  const [rca, setRca] = useState(null);
  const [comments, setComments] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [sectionErrors, setSectionErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [rcaSubTab, setRcaSubTab] = useState("summary");
  const [editStatus, setEditStatus] = useState(ticket.status);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [deleteUpdating, setDeleteUpdating] = useState(false);
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    setCurrentTicket(ticket);
    setEditStatus(ticket.status);
    setActionError("");
  }, [ticket]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setRca(null);
    setComments([]);
    setIncidents([]);
    setSectionErrors({});
    setTab("overview");
    setRcaSubTab("summary");
    Promise.all([
      api(`/tickets/${ticket.id}`)
        .then((data) => {
          if (data && isMounted) {
            setCurrentTicket(data);
            setEditStatus(data.status);
          }
        })
        .catch(() => null),
      api(`/tickets/${ticket.id}/rca`)
        .then((data) => {
          if (isMounted) setRca(data);
        })
        .catch(() => {
          if (isMounted) setSectionErrors((prev) => ({ ...prev, rca: "Could not load RCA details." }));
        }),
      api(`/tickets/${ticket.id}/comments`)
        .then((data) => {
          if (isMounted) setComments(data);
        })
        .catch(() => {
          if (isMounted) setSectionErrors((prev) => ({ ...prev, comments: "Could not load comments." }));
          return [];
        }),
      api(`/tickets/${ticket.id}/incidents`)
        .then((data) => {
          if (isMounted) setIncidents(data);
        })
        .catch(() => {
          if (isMounted) setSectionErrors((prev) => ({ ...prev, incidents: "Could not load linked incidents." }));
          return [];
        }),
    ]).finally(() => {
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [ticket.id]);

  async function handleStatusUpdate() {
    if (editStatus === currentTicket.status) return;
    const validNextStatuses = STATUS_TRANSITIONS[currentTicket.status] || [];
    if (!validNextStatuses.includes(editStatus)) {
      setActionError("That status transition is not allowed.");
      setEditStatus(currentTicket.status);
      return;
    }

    setActionError("");
    setStatusUpdating(true);
    try {
      const u = await api(`/tickets/${currentTicket.id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status: editStatus }),
      });
      if (u) {
        setCurrentTicket(u);
        setEditStatus(u.status);
        if (onUpdated) onUpdated(u);
      }
    } catch {
      setActionError("Could not update ticket status. Please try again.");
      setEditStatus(currentTicket.status);
    } finally {
      setStatusUpdating(false);
    }
  }

  async function handleDeleteTicket() {
    setActionError("");
    setDeleteUpdating(true);
    try {
      await api(`/tickets/${currentTicket.id}`, {
        method: "DELETE",
      });
      const deletedTicket = { ...currentTicket, status: "deleted" };
      setCurrentTicket(deletedTicket);
      setEditStatus("deleted");
      if (onUpdated) onUpdated(deletedTicket, { deleted: true });
      onClose();
    } catch {
      setActionError("Could not delete ticket. Please try again.");
    } finally {
      setDeleteUpdating(false);
    }
  }

  const nextStatuses = STATUS_TRANSITIONS[currentTicket.status] || [];
  const canEditStatus =
    currentTicket.status !== "deleted" && nextStatuses.length > 0;
  const statusOptions = [currentTicket.status, ...nextStatuses].filter(Boolean);
  const actionPending = statusUpdating || deleteUpdating;

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "rca", label: "RCA" },
    { id: "incidents", label: `Incidents (${incidents.length})` },
    { id: "comments", label: `Comments (${comments.length})` },
  ];

  return (
    <Modal title="Ticket Detail" onClose={onClose} width={680}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginBottom: 8,
            flexWrap: "wrap",
          }}
        >
          <StatusBadge status={currentTicket.status} />
          <PriorityBadge priority={currentTicket.priority} />
          <span
            style={{
              fontSize: 11,
              color: "#9CA3AF",
              background: "#F9FAFB",
              padding: "2px 8px",
              borderRadius: 10,
              border: "1px solid #F3F4F6",
            }}
          >
            via {currentTicket.source}
          </span>
        </div>
        <h2
          style={{
            margin: "0 0 6px",
            fontSize: 17,
            fontWeight: 700,
            color: "#111827",
            lineHeight: 1.4,
          }}
        >
          {currentTicket.title}
        </h2>
        <FormattedDescription
          ticket={currentTicket}
        />
      </div>

      {/* Status update */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 18,
          padding: "12px 14px",
          background: "#F8FAFC",
          borderRadius: 8,
          border: "1px solid #E2E8F0",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: "#374151",
            marginRight: 4,
            display: "flex",
            alignItems: "center",
          }}
        >
          Update status:
        </div>
        <select
          value={editStatus}
          onChange={(e) => setEditStatus(e.target.value)}
          disabled={!canEditStatus || actionPending}
          style={{
            flex: 1,
            minWidth: 160,
            padding: "6px 10px",
            border: "1px solid #E5E7EB",
            borderRadius: 6,
            fontSize: 12,
            background: canEditStatus && !actionPending ? "#fff" : "#F3F4F6",
            color: canEditStatus ? "#374151" : "#9CA3AF",
            cursor: canEditStatus && !actionPending ? "pointer" : "not-allowed",
          }}
        >
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {STATUS_LABELS[status] || status}
            </option>
          ))}
        </select>
        <button
          onClick={handleStatusUpdate}
          disabled={
            actionPending ||
            editStatus === currentTicket.status ||
            !canEditStatus
          }
          style={{
            padding: "6px 14px",
            background:
              editStatus !== currentTicket.status && canEditStatus && !actionPending
                ? "#1D4ED8"
                : "#E5E7EB",
            color:
              editStatus !== currentTicket.status && canEditStatus && !actionPending
                ? "#fff"
                : "#9CA3AF",
            border: "none",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 500,
            cursor:
              editStatus !== currentTicket.status && canEditStatus && !actionPending
                ? "pointer"
                : "default",
          }}
        >
          {statusUpdating ? "Saving…" : "Save"}
        </button>
        <button
          onClick={handleDeleteTicket}
          disabled={actionPending || currentTicket.status === "deleted"}
          style={{
            padding: "6px 12px",
            background:
              actionPending || currentTicket.status === "deleted" ? "#F3F4F6" : "#FEF2F2",
            color:
              actionPending || currentTicket.status === "deleted" ? "#9CA3AF" : "#B91C1C",
            border: "1px solid #FECACA",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 500,
            cursor:
              actionPending || currentTicket.status === "deleted" ? "default" : "pointer",
          }}
        >
          {deleteUpdating ? "Deleting..." : "Delete"}
        </button>
        {actionError && (
          <div
            style={{
              flexBasis: "100%",
              fontSize: 12,
              color: "#B91C1C",
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              borderRadius: 6,
              padding: "7px 9px",
            }}
          >
            {actionError}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          borderBottom: "1px solid #F3F4F6",
          marginBottom: 16,
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "8px 14px",
              border: "none",
              background: "none",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? "#1D4ED8" : "#6B7280",
              borderBottom:
                tab === t.id ? "2px solid #1D4ED8" : "2px solid transparent",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "rca" && (
        <div
          role="tablist"
          aria-label="RCA sections"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            borderBottom: "1px solid #E2E8F0",
            marginTop: -8,
            marginBottom: 14,
          }}
        >
          {[
            { id: "summary", label: "Summary" },
            { id: "specialists", label: "Domain Specialist Insights" },
            { id: "next-steps", label: "Recommended Steps" },
            { id: "runbooks", label: "Correlated KB" },
          ].map((subTab) => (
            <button
              key={subTab.id}
              type="button"
              role="tab"
              aria-selected={rcaSubTab === subTab.id}
              onClick={() => setRcaSubTab(subTab.id)}
              style={{
                padding: "7px 10px",
                border: "none",
                borderBottom: rcaSubTab === subTab.id ? "2px solid #2563EB" : "2px solid transparent",
                background: "transparent",
                color: rcaSubTab === subTab.id ? "#1D4ED8" : "#64748B",
                fontSize: 11,
                fontWeight: rcaSubTab === subTab.id ? 600 : 400,
                cursor: "pointer",
              }}
            >
              {subTab.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={52} style={{ borderRadius: 8 }} />
          ))}
        </div>
      ) : (
        <>
          {tab === "overview" && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              {[
                { label: "Ticket ID", value: currentTicket.id, mono: true, icon: "ticket", color: "#64748B" },
                {
                  label: "Status",
                  value: <StatusBadge status={currentTicket.status} />,
                  icon: "info",
                  color: "#1D4ED8"
                },
                {
                  label: "Priority",
                  value: <PriorityBadge priority={currentTicket.priority} />,
                  icon: "alert",
                  color: "#DC2626"
                },
                { label: "Source", value: currentTicket.source, icon: "globe", color: "#0D9488" },
                ...(currentTicket.application_name || currentTicket.application_id
                  ? [
                      {
                        label: "Application",
                        value: currentTicket.application_name || currentTicket.application_id,
                        icon: "layers",
                        color: "#7C3AED",
                        mono: !currentTicket.application_name,
                      },
                    ]
                  : []),
                ...(currentTicket.service_name || currentTicket.service_id
                  ? [
                      {
                        label: "Service",
                        value: currentTicket.service_name || currentTicket.service_id,
                        icon: "zap",
                        color: "#D97706",
                        mono: !currentTicket.service_name,
                      },
                    ]
                  : []),
                {
                  label: "Created",
                  value: currentTicket.created_at
                    ? new Date(currentTicket.created_at).toLocaleString("en-IN")
                    : "—",
                  icon: "clock",
                  color: "#475569"
                },
                {
                  label: "Updated",
                  value: currentTicket.updated_at
                    ? new Date(currentTicket.updated_at).toLocaleString("en-IN")
                    : "—",
                  icon: "refresh",
                  color: "#6B7280"
                },
              ].map((f) => (
                <div
                  key={f.label}
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    borderRadius: 10,
                    padding: "12px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.01)",
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: "#F8FAFC",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #F1F5F9",
                    }}
                  >
                    <Icon name={f.icon} size={15} color={f.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#94A3B8",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        marginBottom: 2,
                      }}
                    >
                      {f.label}
                    </div>
                    {typeof f.value === "string" ? (
                      <div
                        style={{
                          fontSize: 13,
                          color: "#1E293B",
                          fontFamily: f.mono ? "monospace" : undefined,
                          wordBreak: "break-all",
                          fontWeight: 500,
                        }}
                      >
                        {f.value}
                      </div>
                    ) : (
                      f.value
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {tab === "rca" &&
            (sectionErrors.rca ? (
              <EmptyState icon="alert" title="RCA could not be loaded" subtitle={sectionErrors.rca} />
            ) : rca ? (
              <RCAPanel
                rca={rca}
                context={currentTicket}
                section={{
                  summary: "rca",
                  specialists: "specialists",
                  "next-steps": "next-steps",
                  runbooks: "runbooks",
                }[rcaSubTab]}
              />
            ) : (
              <EmptyState
                icon="search"
                title="No RCA available"
                subtitle="Root cause analysis hasn't been run yet."
              />
            ))}
          {tab === "incidents" && (sectionErrors.incidents ? (
            <EmptyState icon="alert" title="Incidents could not be loaded" subtitle={sectionErrors.incidents} />
          ) : <IncidentsPanel incidents={incidents} />)}
          {tab === "comments" && (sectionErrors.comments ? (
            <EmptyState icon="alert" title="Comments could not be loaded" subtitle={sectionErrors.comments} />
          ) : <CommentsPanel comments={comments} />)}
        </>
      )}
    </Modal>
  );
}
